import { useState } from "react";
import { Music, MapPin, CalendarDays, Plus } from "lucide-react";
import { Reveal, SectionHeading, Modal, RoseMark } from "./common";
import { FRESCHI, STRUMENTI, IMAGES } from "../data/content";

export const Affreschi = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="affreschi" className="relative bg-sand py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <SectionHeading
            eyebrow="Capitolo III"
            title="Gli Affreschi"
            testid="section-affreschi-title"
          />
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-5 text-base sm:text-lg text-taupe leading-relaxed">
              <p>
                Se la facciata conquista lo sguardo, è l'interno che lascia
                senza fiato:{" "}
                <strong className="text-espresso font-semibold">
                  volte, pareti e archi sono ricoperti da uno dei cicli di
                  affreschi più estesi del Medioevo italiano
                </strong>
                , realizzato nel corso del Quattrocento. È soprattutto per
                questa decorazione pittorica che la Basilica è considerata un
                monumento di importanza nazionale.
              </p>
              <p className="text-sm sm:text-base text-fog border-l-2 border-gold pl-4">
                Una nota di rigore: le fonti non consentono di attribuire gli
                affreschi con certezza a singoli artisti. Gli studiosi parlano
                di più botteghe attive tra la fine del Trecento e la prima metà
                del Quattrocento, legate alla cultura giottesca e alla pittura
                angioina.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FRESCHI.map((f, i) => (
            <Reveal key={f.id} delay={(i % 3) * 0.08}>
              <button
                onClick={() => setSelected(f)}
                data-testid={`fresco-card-${f.id}`}
                className="group w-full h-full text-left bg-ivory border border-stone hover:border-gold transition-all duration-500 hover:shadow-[0_16px_45px_rgba(43,35,31,0.10)] hover:-translate-y-1 overflow-hidden flex flex-col"
              >
                <div className="img-frame aspect-[4/3]">
                  <img
                    src={f.image}
                    alt={f.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 flex items-center gap-2 text-ivory/90">
                    <CalendarDays size={13} className="text-goldlight" />
                    <span className="font-mono text-[11px] tracking-widest uppercase">
                      {f.period}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-serif text-xl font-semibold text-espresso">
                      {f.title}
                    </h3>
                    <Plus
                      size={16}
                      className="text-gold flex-shrink-0 transition-transform duration-500 group-hover:rotate-90"
                    />
                  </div>
                  <p className="mt-2 text-sm text-taupe leading-relaxed flex-1">{f.brief}</p>
                  <div className="mt-4 flex items-center gap-2 text-fog">
                    <MapPin size={13} className="text-gold" />
                    <span className="text-xs tracking-wide">{f.place}</span>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-24 relative bg-espresso overflow-hidden">
        <RoseMark className="absolute -bottom-16 -left-16 w-80 h-80 text-gold/10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="img-frame relative">
              <img
                src={IMAGES.frescoAngels}
                alt="Angeli dipinti in un affresco medievale"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-gold/30" />
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-gold/40 -z-0 pointer-events-none" aria-hidden="true" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <div className="flex items-center gap-3">
                <Music size={18} className="text-goldlight" />
                <span className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase text-goldlight">
                  Approfondimento speciale
                </span>
              </div>
              <h3
                data-testid="angeli-title"
                className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-ivory tracking-tight"
              >
                Gli Angeli musicanti
              </h3>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-base sm:text-lg text-ivory/80 leading-relaxed">
                Nelle vele delle volte, schiere di angeli suonano gli strumenti
                del loro tempo. Non è solo poesia: gli strumenti sono dipinti
                con tale accuratezza che gli{" "}
                <strong className="text-ivory font-semibold">
                  storici della musica li studiano come un documento prezioso
                </strong>{" "}
                sugli strumenti musicali medievali, sulle loro forme e sul modo
                in cui venivano suonati.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 flex flex-wrap gap-2">
                {STRUMENTI.map((s) => (
                  <span
                    key={s}
                    data-testid={`strumento-${s.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-mono text-xs tracking-widest uppercase px-4 py-2 border border-gold/40 text-goldlight rounded-full"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        image={selected?.image}
        title={selected?.title ?? ""}
        testid="fresco-modal"
      >
        {selected && (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs tracking-widest uppercase text-gold not-italic">
              <span className="flex items-center gap-2">
                <CalendarDays size={13} /> {selected.period}
              </span>
              <span className="flex items-center gap-2">
                <MapPin size={13} /> {selected.place}
              </span>
            </div>
            <p>{selected.detail}</p>
          </>
        )}
      </Modal>
    </section>
  );
};
